import React, { useEffect, useState } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import { API_BASE_URL } from "../../config/apiConfig";
import { FaArrowLeft, FaSave, FaPrint } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { RingLoader } from "react-spinners";

const ExamPerformanceIndividual = () => {
    const [rawData, setRawData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTopic, setSelectedTopic] = useState("");
    const [selectedSubject, setSelectedSubject] = useState("");
    const navigate = useNavigate();
    const user_id = localStorage.getItem("child_id");


    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }, []); // runs only on mount


    useEffect(() => {
        const fetchUserResults = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}/api/parents/user/${user_id}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${localStorage.getItem("token")}`,
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to fetch user results.");
                }

                const resultData = await response.json();
                setRawData(resultData.data);
            } catch (error) {
                console.error("Error fetching results:", error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchUserResults();
    }, []);

    const goBack = () => navigate(-1);

    // Extract unique topics and subjects from the actual data structure
    const topics = Array.from(new Set(rawData.map((item) => item.topic).filter(Boolean)));
    const subjects = Array.from(new Set(rawData.map((item) => item.subject).filter(Boolean)));

    // Filtered data for questions table
    const filteredQuestions = rawData.filter((item) => {
        const topicMatch = selectedTopic === "" || item.topic === selectedTopic;
        const subjectMatch = selectedSubject === "" || item.subject === selectedSubject;
        return topicMatch && subjectMatch;
    });

    // Process data to get exam-level statistics
    const examStats = Object.values(
        rawData.reduce((acc, item) => {
            const testId = item.test_id;
            if (!acc[testId]) {
                acc[testId] = {
                    id: testId,
                    date: item.test?.createdAt || item.createdAt,
                    mode: item.mode,
                    subjects: new Set(),
                    topics: new Set(),
                    totalQuestions: 0,
                    correctAnswers: 0,
                };
            }

            const exam = acc[testId];
            exam.totalQuestions += 1;
            exam.correctAnswers += item.is_correct ? 1 : 0;
            if (item.subject) exam.subjects.add(item.subject);
            if (item.topic) exam.topics.add(item.topic);

            return acc;
        }, {})
    ).map((exam) => ({
        ...exam,
        score: exam.totalQuestions > 0 ? (exam.correctAnswers / exam.totalQuestions) * 100 : 0,
        date: new Date(exam.date).toLocaleDateString(),
        subjectsList: Array.from(exam.subjects).join(", "),
        topicsList: Array.from(exam.topics).join(", "),
        subjectCount: exam.subjects.size,
        topicCount: exam.topics.size,
    })).sort((a, b) => new Date(a.date) - new Date(b.date));

    const handleSave = () => console.log("Save report functionality coming soon!");
    const handlePrint = () => window.print();

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-100">
                <RingLoader color="#4A90E2" size={50} />
                <p className="ml-4 text-blue-600 font-medium">Loading your progress...</p>
            </div>
        );
    }

    if (rawData.length === 0) {
        return (
            <div className="flex flex-col h-full min-h-screen">
                <div className="flex items-center justify-between mb-6 px-4 py-2 bg-white shadow-sm">
                    <button
                        onClick={goBack}
                        className="flex items-center gap-2 text-[#2148C0] hover:text-blue-600 font-medium transition"
                    >
                        <FaArrowLeft className="text-xl" />
                        Back
                    </button>
                </div>
                <div className="flex items-center justify-center flex-grow bg-gray-100">
                    <p className="text-lg font-medium text-gray-600">
                        No exam performance history available.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-8">
            {/* Header */}
            <div className="flex flex-row items-center justify-between mb-6 gap-4">
                {/* Left: Back button */}
                <div className="w-auto flex justify-start">
                    <button
                        onClick={goBack}
                        className="flex items-center gap-2 text-[#2148C0] hover:text-blue-600 font-medium transition"
                    >
                        <FaArrowLeft className="text-xl" />
                        Back
                    </button>
                </div>

                {/* Right: Save & Print */}
                <div className="w-full md:w-auto flex justify-end  gap-4">
                    <button
                        onClick={handleSave}
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition"
                    >
                        <FaSave className="text-xl" /> Save
                    </button>
                    <button
                        onClick={handlePrint}
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition"
                    >
                        <FaPrint className="text-xl" /> Print
                    </button>
                </div>
            </div>


            <h2 className="text-3xl md:text-4xl font-extrabold text-[#2148C0] text-center mb-3">
                Exam Performance History
            </h2>
            <p className="text-center text-gray-500 max-w-2xl mx-auto mb-8 text-sm md:text-base">
                Every exam your child has completed on Teechaa, from the earliest one on the left
                to the most recent on the right. Use the chart to spot trends and the tables below
                to dig into individual exams and questions.
            </p>

            {/* Performance Chart */}
            <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-8">
                <h2 className="text-xl font-semibold text-gray-600 mb-1">
                    Performance Over Time (%)
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                    Each point is one exam. A rising line means their scores are improving; a
                    dip is a good conversation starter about what tripped them up.
                </p>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={examStats} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fill: "#374151" }} />
                        <YAxis tick={{ fill: "#374151" }} domain={[0, 100]} />
                        <Tooltip formatter={(value) => `${value.toFixed(1)}%`} labelFormatter={(label) => `Date: ${label}`} />
                        <Legend />
                        <Line type="monotone" dataKey="score" stroke="#3b82f6" strokeWidth={2} activeDot={{ r: 8 }} />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Exam Summary Table */}
            <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-8 overflow-x-auto">
                <h2 className="text-xl font-semibold text-gray-600 mb-1">Exam Summary</h2>
                <p className="text-sm text-gray-500 mb-4">
                    One row per exam. <strong>Score</strong> is the percentage they got right —
                    <span className="text-green-700"> green ≥ 70%</span>,
                    <span className="text-yellow-700"> yellow 50–69%</span>,
                    <span className="text-red-700"> red &lt; 50%</span>.
                </p>
                <table className="table-auto w-full border-collapse border border-gray-200 text-sm md:text-base">
                    <thead>
                        <tr className="bg-gray-100 text-left">
                            <th className="border border-gray-200 px-2 md:px-4 py-2">Test ID</th>
                            <th className="border border-gray-200 px-2 md:px-4 py-2">Date</th>
                            <th className="border border-gray-200 px-2 md:px-4 py-2">Mode</th>
                            <th className="border border-gray-200 px-2 md:px-4 py-2">Questions</th>
                            <th className="border border-gray-200 px-2 md:px-4 py-2">Correct</th>
                            <th className="border border-gray-200 px-2 md:px-4 py-2">Score (%)</th>
                            <th className="border border-gray-200 px-2 md:px-4 py-2">Subjects</th>
                            <th className="border border-gray-200 px-2 md:px-4 py-2">Topics</th>
                        </tr>
                    </thead>
                    <tbody>
                        {examStats.map((exam) => (
                            <tr key={exam.id} className="hover:bg-gray-50 transition">
                                <td className="border border-gray-200 px-2 md:px-4 py-2 font-medium">
                                    {exam.id}
                                </td>
                                <td className="border border-gray-200 px-2 md:px-4 py-2">{exam.date}</td>
                                <td className="border border-gray-200 px-2 md:px-4 py-2 capitalize">
                                    {exam.mode}
                                </td>
                                <td className="border border-gray-200 px-2 md:px-4 py-2 text-center">
                                    {exam.totalQuestions}
                                </td>
                                <td className="border border-gray-200 px-2 md:px-4 py-2 text-center">
                                    {exam.correctAnswers}
                                </td>
                                <td className="border border-gray-200 px-2 md:px-4 py-2 text-center font-medium">
                                    <span className={`px-2 py-1 rounded text-sm ${exam.score >= 70 ? 'bg-green-100 text-green-800' :
                                        exam.score >= 50 ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-red-100 text-red-800'
                                        }`}>
                                        {exam.score.toFixed(1)}%
                                    </span>
                                </td>
                                <td className="border border-gray-200 px-2 md:px-4 py-2 text-sm">
                                    {exam.subjectsList || 'N/A'}
                                </td>
                                <td className="border border-gray-200 px-2 md:px-4 py-2 text-sm max-w-xs truncate" title={exam.topicsList}>
                                    {exam.topicsList || 'N/A'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-600 mb-4">Filter Questions</h3>
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Subject:</label>
                        <select
                            value={selectedSubject}
                            onChange={(e) => setSelectedSubject(e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">All Subjects</option>
                            {subjects.map((subject) => (
                                <option key={subject} value={subject}>{subject}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Topic:</label>
                        <select
                            value={selectedTopic}
                            onChange={(e) => setSelectedTopic(e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">All Topics</option>
                            {topics.map((topic) => (
                                <option key={topic} value={topic}>{topic}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="mt-4 text-sm text-gray-600">
                    Showing {filteredQuestions.length} of {rawData.length} questions
                    {selectedSubject && <span className="font-medium"> • Subject: {selectedSubject}</span>}
                    {selectedTopic && <span className="font-medium"> • Topic: {selectedTopic}</span>}
                </div>
            </div>

            {/* Questions Table */}
            <div className="bg-white rounded-lg shadow p-4 md:p-6 overflow-x-auto">
                <h2 className="text-xl font-semibold text-gray-600 mb-1">
                    Question Details
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                    Every question your child has seen, with what they picked, the correct
                    answer and the difficulty. A ✓ means they got it right, ✗ means wrong.
                    Filter above to focus on one subject or topic.
                </p>
                <table className="table-auto w-full border-collapse border border-gray-200 text-sm md:text-base">
                    <thead>
                        <tr className="bg-gray-100 text-left">
                            <th className="border border-gray-200 px-2 md:px-4 py-2">Test ID</th>
                            <th className="border border-gray-200 px-2 md:px-4 py-2">Subject</th>
                            <th className="border border-gray-200 px-2 md:px-4 py-2">Topic</th>
                            <th className="border border-gray-200 px-2 md:px-4 py-2">Question</th>
                            <th className="border border-gray-200 px-2 md:px-4 py-2">Your Answer</th>
                            <th className="border border-gray-200 px-2 md:px-4 py-2">Correct Answer</th>
                            <th className="border border-gray-200 px-2 md:px-4 py-2">Result</th>
                            <th className="border border-gray-200 px-2 md:px-4 py-2">Level</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredQuestions.map((item) => (
                            <tr key={item.user_answer_id} className="hover:bg-gray-50 transition">
                                <td className="border border-gray-200 px-2 md:px-4 py-2 font-medium text-center">
                                    {item.test_id}
                                </td>
                                <td className="border border-gray-200 px-2 md:px-4 py-2">
                                    {item.subject || 'N/A'}
                                </td>
                                <td className="border border-gray-200 px-2 md:px-4 py-2 text-sm">
                                    {item.topic || 'N/A'}
                                </td>
                                <td className="border border-gray-200 px-2 md:px-4 py-2 max-w-md">
                                    <div className="truncate" title={item.quiz?.question}>
                                        {item.quiz?.question || 'Question not available'}
                                    </div>
                                </td>
                                <td className="border border-gray-200 px-2 md:px-4 py-2 text-center">
                                    {item.selected_option ? (
                                        <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                                            {item.selected_option}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400 italic">Not answered</span>
                                    )}
                                </td>
                                <td className="border border-gray-200 px-2 md:px-4 py-2 text-center">
                                    <span className="inline-block px-2 py-1 bg-green-100 text-green-800 rounded text-sm">
                                        {item.quiz?.correctAnswer || 'N/A'}
                                    </span>
                                </td>
                                <td className="border border-gray-200 px-2 md:px-4 py-2 text-center">
                                    <span className={`text-2xl ${item.is_correct ? 'text-green-600' : 'text-red-600'}`}>
                                        {item.is_correct ? '✓' : '✗'}
                                    </span>
                                </td>
                                <td className="border border-gray-200 px-2 md:px-4 py-2 text-center">
                                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${item.quiz?.level === 'Easy' ? 'bg-green-100 text-green-800' :
                                        item.quiz?.level === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                            item.quiz?.level === 'Hard' ? 'bg-red-100 text-red-800' :
                                                'bg-gray-100 text-gray-800'
                                        }`}>
                                        {item.quiz?.level || 'N/A'}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ExamPerformanceIndividual;